import { array } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { useEffect } from 'react';
import { FormStateProxy, useFormContext } from 'react-hook-form';
import { useCalendarService } from '@mews-commander/ui';
import { useDateFieldTransform } from '@mews/framework';
import { setEarliestAcceptableEndDate } from '../../utils';
import { DateIntervalFieldsProps } from './types';
import { useCreateDateIntervalRestrictions } from './useCreateDateIntervalRestrictions';

type Props = Exclude<DateIntervalFieldsProps, 'inline'>;

export const useValidateDateInterval = ({
    autocorrect,
    startDateProps,
    endDateProps,
    minDayDifference,
    maxDayDifference,
    correctionPolicy = setEarliestAcceptableEndDate,
    autofillDayDifference = 1,
    minDate,
    maxDate,
    mode,
}: Props) => {
    const {
        formState: { dirtyFields },
        setValue,
    } = useFormContext();

    const transformDate = useDateFieldTransform(mode);

    const { startDateValue, endDateValue, maxEndDate, minEndDate } = useCreateDateIntervalRestrictions({
        minDate,
        maxDate,
        minDayDifference,
        maxDayDifference,
        mode,
        startDateProps,
        endDateProps,
    });

    const calendarService = useCalendarService();

    useEffect(() => {
        const {
            addDays,
            isValidDateString,
            getDateFromDateString,
            isDateLessOrEqual,
            isDateGreaterOrEqual,
            isDateGreater,
            getDateString,
        } = calendarService;

        const isStartDateDirty = isDateDirty({ dateInputName: startDateProps.name, dirtyFields });
        const isEndDateDirty = isDateDirty({ dateInputName: endDateProps.name, dirtyFields });

        if (
            autocorrect &&
            (isStartDateDirty || isEndDateDirty) &&
            typeof startDateValue === 'string' &&
            isValidDateString(startDateValue) &&
            typeof endDateValue === 'string' &&
            isValidDateString(endDateValue)
        ) {
            const startDate = getDateFromDateString(startDateValue);
            const endDate = getDateFromDateString(endDateValue);

            const isWithinMinDayDifference = minDayDifference
                ? isDateLessOrEqual(addDays(startDate, minDayDifference), endDate)
                : true;
            const isWithinMaxDayDifference = maxDayDifference
                ? isDateGreaterOrEqual(addDays(startDate, maxDayDifference), endDate)
                : true;

            if (isDateGreater(startDate, endDate) || !isWithinMinDayDifference || !isWithinMaxDayDifference) {
                const { newStartDate = null, newEndDate = null } = correctionPolicy({
                    startDate,
                    getCalendarService: () => calendarService,
                    autofillDayDifference,
                    minDayDifference,
                    maxDayDifference,
                });

                if (newStartDate !== startDate && newStartDate) {
                    const newStartDateString = getDateString(newStartDate);
                    setValue(startDateProps.name, transformDate?.input?.(newStartDateString) ?? newStartDateString);
                }
                if (newEndDate !== endDate && newEndDate) {
                    const newEndDateString = getDateString(newEndDate);
                    setValue(endDateProps.name, transformDate?.input?.(newEndDateString) ?? newEndDateString);
                }
            }
        }
    }, [
        autocorrect,
        autofillDayDifference,
        calendarService,
        correctionPolicy,
        dirtyFields,
        endDateProps.name,
        endDateValue,
        maxDayDifference,
        minDayDifference,
        setValue,
        startDateProps.name,
        startDateValue,
        transformDate,
    ]);

    return {
        minEndDate,
        maxEndDate,
    };
};

const isDateDirty = ({
    dateInputName,
    dirtyFields,
}: {
    dateInputName: string;
    dirtyFields: FormStateProxy['dirtyFields'];
}) =>
    pipe(
        dateInputName.split('.'),
        array.reduce(dirtyFields, (obj, key) => obj && obj[key])
    );
